import { useEffect, useRef } from 'react';
import fitty, { FittyInstance } from 'fitty';

interface UseFittyVisibleOptions {
  minSize: number;
  maxSize: number;
  multiLine?: boolean;
}

let globalResizeTimer: NodeJS.Timeout | null = null;
const activeFittyInstances = new Set<FittyInstance>();

const handleGlobalResize = () => {
  if (globalResizeTimer) clearTimeout(globalResizeTimer);
  globalResizeTimer = setTimeout(() => {
    activeFittyInstances.forEach(instance => {
      try {
        instance.fit();
      } catch {
        activeFittyInstances.delete(instance);
      }
    });
  }, 150);
};

if (typeof window !== 'undefined') {
  window.removeEventListener('resize', handleGlobalResize);
  window.addEventListener('resize', handleGlobalResize);
}

/**
 * Hook genérico para usar fitty com qualquer tipo de elemento HTML.
 */
export const useFittyVisible = <T extends HTMLElement = HTMLDivElement>(
  options: UseFittyVisibleOptions,
) => {
  const elRef = useRef<T | null>(null);
  const fittyInstance = useRef<FittyInstance | null>(null);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    let timeoutId: NodeJS.Timeout | null = null;

    const initializeFitty = () => {
      if (!el) return;
      if (fittyInstance.current) return;

      const instance = fitty(el, {
        ...options,
        observeMutations: {
          attributes: true,
          childList: true,
          subtree: true,
          characterData: true,
        },
      });

      fittyInstance.current = instance;
      activeFittyInstances.add(instance);
    };

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            timeoutId = setTimeout(() => initializeFitty(), 50);
          }
        }
      },
      {
        threshold: 0.1,
        rootMargin: '400px',
      },
    );

    observer.observe(el);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      observer.disconnect();
      const instance = fittyInstance.current;
      if (instance) {
        activeFittyInstances.delete(instance);
        instance.unsubscribe();
      }
    };
  }, [options.minSize, options.maxSize, options.multiLine]);

  return elRef;
};
